<script setup lang="ts">
import { computed } from 'vue';

import { useGlobalStyle, useTemplate } from '@/store/templateData';

import facebookIconImage from '../../assets/icons/facebook.svg';
import lineIconImage from '../../assets/icons/line.svg';
import twitterIconImage from '../../assets/icons/twitter.svg';

interface ModuleShareProps {
  module: {
    components: {
      title: {
        text: string;
      };
      line: {
        enabled: boolean;
      };
      twitter: {
        enabled: boolean;
      };
      facebook: {
        enabled: boolean;
      };
    };
  };
}

defineOptions({
  name: 'ModuleShareDisplay'
});

const props = withDefaults(defineProps<ModuleShareProps>(), {
  module: () => ({
    components: {
      title: {
        text: '',
      },
      line: {
        enabled: true,
      },
      twitter: {
        enabled: true,
      },
      facebook: {
        enabled: true,
      },
    },
  }),
});

const components = computed(() => props.module.components);
const { handsupConfig } = useTemplate();
const postShareUrl = computed(() => handsupConfig.value?.post?.share_url);
const shareUrl = computed(() => {
  // 1. 先取得 Post 的 share_url
  // 2. 若無設定，則取當下的網址
  const url = postShareUrl.value ?? location.href;
  return encodeURIComponent(url);
});
const shareUrlLine = computed(() => {
  return `https://social-plugins.line.me/lineit/share?url=${shareUrl.value}`;
});
const shareUrlTwitter = computed(() => {
  return `https://twitter.com/intent/tweet?text=${shareUrl.value}`;
});
const shareUrlFacebook = computed(() => {
  return `https://www.facebook.com/sharer.php?u=${shareUrl.value}`;
});

const { titleStyle } = useGlobalStyle();
</script>

<template>
<div class="module-share-display">
  <div
    class="module-share-display__title"
    v-if="components.title.text"
    :style="titleStyle"
  >
    {{ components.title.text }}
  </div>
  <div class="module-share-display__share-list">
    <a
      class="module-share-display__social-icon"
      v-if="components.line.enabled"
      :href="shareUrlLine"
      target="_blank"
    >
      <el-image :src="lineIconImage"/>
    </a>
    <a
      class="module-share-display__social-icon"
      v-if="components.twitter.enabled"
      :href="shareUrlTwitter"
      target="_blank"
    >
      <el-image :src="twitterIconImage"/>
    </a>
    <a
      class="module-share-display__social-icon"
      v-if="components.facebook.enabled"
      :href="shareUrlFacebook"
      target="_blank"
    >
      <el-image :src="facebookIconImage"/>
    </a>
  </div>
</div>
</template>
<style lang="scss" scoped>
$desktop-fixed-width: 1075px;

.module-share-display {
  @apply flex items-center flex-col;
  @apply px-5 pt-4 pb-8;

  &__title {
    @apply mb-4 font-medium break-all;
    @apply text-base text-center whitespace-pre-line;

    word-wrap: break-word;
  }

  &__share-list {
    @apply flex space-x-6;
  }

  &__social-icon {
    @apply block w-12 h-12 hover:opacity-70;
  }
}

// tablet
.tablet .module-share-display {
  @apply px-10;
}

// PC - 1024px
.laptop .module-share-display {
  padding-left: 10%;
  padding-right: 10%;

  @apply py-8;
}

// PC - 1440px
.desktop .module-share-display {
  width: $desktop-fixed-width;

  @apply py-8;
}
</style>
